"use client";

import React from "react";
import { ArrowRight, Award, CircleCheck as CheckCircle2, GraduationCap, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "Qualification & Lifecycle Services",
  path: "/QualificationLifecycleServicesPage",
  badge: "Compliance & Validation",
  description: "Comprehensive analytical instrument qualification, IQ/OQ/PQ execution, calibration, and long-term lifecycle maintenance for regulated environments.",
  icon: ShieldCheck,
  features: ["IQ / OQ / PQ validation protocols", "GAMP & 21 CFR Part 11 readiness", "Preventive maintenance schedules", "Recalibration & requalification support"],
  ctaText: "Explore Qualification Services"
}, {
  title: "Training & Application Support",
  path: "/TrainingApplicationSupportPage",
  badge: "Method & Operator Training",
  description: "Expert-led technical training, method development, sample testing feasibility, and ongoing application support for laboratory and process operations.",
  icon: GraduationCap,
  features: ["Hands-on operator & analyst training", "Analytical method development & transfer", "Feasibility studies & sample screening", "Direct consultation with application specialists"],
  ctaText: "Explore Training & Support"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
              Lifecycle & Operational Support
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Related Technical Services
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Ensure your analytical instrumentation delivers maximum reliability, regulatory compliance, and peak operational performance at every stage.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.title} data-index={index} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.15
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200 bg-card rounded-xl">
                  <div>
                    <CardHeader className="space-y-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="size-12 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center">
                          <Icon className="size-6 text-primary" />
                        </div>
                        <Badge variant="secondary" className="font-normal text-xs">
                          {service.badge}
                        </Badge>
                      </div>
                      <div>
                        <CardTitle className="text-xl md:text-2xl font-bold text-card-foreground">
                          {service.title}
                        </CardTitle>
                        <CardDescription className="mt-2.5 text-sm md:text-base text-muted-foreground leading-relaxed">
                          {service.description}
                        </CardDescription>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-2 pb-6">
                      <div className="space-y-2.5 border-t border-border/60 pt-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                          Key Capabilities
                        </p>
                        {service.features.map((feature, featureIndex) => <div key={feature} data-index={featureIndex} className="flex items-start gap-2.5 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2">
                    <Button asChild className="w-full justify-between" size="lg">
                      <Link to={service.path}>
                        <span>{service.ctaText}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}